import { type Campo, type Registro, TablaPivote } from '@/components/dominio/TablaPivote';
import { FiltroPeriodos } from '@/components/filtros/FiltroPeriodos';
import { Card, CardContent } from '@/components/ui/card';
import { usePeriodosDisponibles } from '@/hooks/useAreaCosteo';
import { useMaquinariaUtilizacion } from '@/hooks/useMaquinariaUtilizacion';
import type { PeriodoMaqSel } from '@/lib/cliente';
import { CircleNotch } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

const PERIODO_MAQ_VACIO: PeriodoMaqSel = { aniosSel: [], mesesSel: [], diasSel: [] };

const NOMBRE_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];
/** Etiqueta de mes que ordena cronológicamente: "03 Mar". */
function etiquetaMes(mes: number): string {
  return `${String(mes).padStart(2, '0')} ${NOMBRE_MES[mes - 1] ?? ''}`.trim();
}

const ETIQUETA_ORIGEN_USO: Record<string, string> = {
  PARTE_TRABAJO: 'Parte de Trabajo',
  ASSEMBLY: 'Consumo Ensamblado',
};

/** Etiqueta de la partida: "1.1 — Fundaciones" si display no repite el código. */
function etiquetaPartida(taskNo: string | null, display: string | null): string {
  if (!taskNo) return '(—)';
  if (display && !display.startsWith(taskNo)) return `${taskNo} — ${display}`;
  return display ?? taskNo;
}

/** Catálogo de campos disponibles en la pivot (dimensiones + métricas). */
const CAMPOS: Campo[] = [
  { id: 'Máquina', etiqueta: 'Máquina', tipo: 'dimension' },
  { id: 'Tipo Máquina', etiqueta: 'Tipo Máquina', tipo: 'dimension' },
  { id: 'Obra', etiqueta: 'Obra', tipo: 'dimension' },
  { id: 'Partida', etiqueta: 'Partida', tipo: 'dimension' },
  { id: 'Origen Uso', etiqueta: 'Origen Uso', tipo: 'dimension' },
  { id: 'Año', etiqueta: 'Año', tipo: 'dimension' },
  { id: 'Mes', etiqueta: 'Mes', tipo: 'dimension' },
  { id: 'Horas', etiqueta: 'Horas', tipo: 'metrica', formato: 'numero' },
  { id: 'Monto', etiqueta: 'Monto', tipo: 'metrica', formato: 'moneda' },
];

/**
 * Tabla Dinámica de Maquinaria: pivote configurable (drag-and-drop) sobre el
 * resumen de utilización por máquina (endpoint maquinaria-utilizacion, ya
 * agrupado por máquina/obra/partida/origen de uso/mes). "Monto" es la misma
 * transacción vista desde dos ángulos: ingreso para la máquina, costo para la
 * obra (ver Costo.montoIngresoMaquinaLcy/montoCostoObraLcy).
 *
 * Para origenUso='ASSEMBLY' (consumo vía ensamblados), Obra siempre muestra
 * "MAQ" y Partida "(—)": ninguna de las entidades de BC disponibles hoy expone
 * la partida para ese canal, y la obra real tampoco — solo el centro de costo
 * interno de Maquinaria (ver docs/maquinaria-contrato-datos.md).
 */
export function MaquinariaPivotPantalla() {
  const [periodoMaq, setPeriodoMaq] = useState<PeriodoMaqSel>(PERIODO_MAQ_VACIO);
  const { data: fechasData } = usePeriodosDisponibles();
  const { data, isLoading, error } = useMaquinariaUtilizacion(periodoMaq);

  const datos = useMemo<Registro[]>(() => {
    return (data?.data ?? []).map((r) => ({
      Máquina: r.nombreMaquina ? `${r.codigoMaquina} — ${r.nombreMaquina}` : r.codigoMaquina,
      'Tipo Máquina': r.tipoMaquina ?? '(—)',
      Obra: r.proyectoDisplay ? `${r.worksNo} — ${r.proyectoDisplay}` : r.worksNo,
      Partida: etiquetaPartida(r.taskNo, r.tareaDisplay),
      'Origen Uso': ETIQUETA_ORIGEN_USO[r.origenUso] ?? r.origenUso,
      Año: String(r.anio),
      Mes: etiquetaMes(r.mes),
      Horas: r.totalHoras,
      Monto: r.totalIngresoLcy,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tabla Dinámica de Maquinaria</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Arrastrá dimensiones a Filas/Columnas y métricas a Métricas. Cruzá Máquina × Obra o
          Máquina × Partida para ver costos por máquina/obra; Máquina × Mes para ver utilización.
          "Monto" es ingreso para la máquina y costo para la obra a la vez. Respeta los filtros
          globales.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Período (solo Maquinaria):</span>
        <FiltroPeriodos
          fechasDisponibles={fechasData?.data ?? []}
          aniosSel={periodoMaq.aniosSel}
          mesesSel={periodoMaq.mesesSel}
          diasSel={periodoMaq.diasSel}
          onChange={setPeriodoMaq}
        />
        <span className="text-xs text-muted-foreground">
          — sin selección, muestra el histórico completo.
        </span>
      </div>

      <TablaPivote
        datos={datos}
        campos={CAMPOS}
        filasIniciales={['Máquina']}
        columnasIniciales={['Obra']}
        metricasIniciales={['Horas', 'Monto']}
        tituloExport="Tabla Dinámica de Maquinaria"
        nombreArchivoExport="tabla-dinamica-maquinaria"
        favoritasKey="maquinaria"
      />
    </div>
  );
}
